import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { X, RefreshCw, Headphones } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { chatFlow } from "./chatFlow";
import "./styles.css";

const SUPPORT_NUMBER = "5551987718296"; // ⚠️ ATUALIZE COM SEU NÚMERO (apenas números, com DDI e DDD)

const SupportChat = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [currentStep, setCurrentStep] = useState("start");
  const [history, setHistory] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Verifica se está na página de chats
  const isChatsPage = location.pathname === '/chats';

  // Mostra hint a cada 5 minutos se o usuário estiver logado
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      if (!isOpen) {
        setShowHint(true);
        // Esconde o hint automaticamente após 10 segundos
        setTimeout(() => setShowHint(false), 10000);
      }
    }, 300000); // 5 minutos = 300.000ms

    return () => clearInterval(interval);
  }, [user, isOpen]);

  // Inicializa o chat com a mensagem de boas-vindas
  useEffect(() => {
    if (history.length === 0) {
      addBotMessage(chatFlow.start.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll para o fim das mensagens
  useEffect(() => {
    scrollToBottom();
  }, [history, isTyping, isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const addBotMessage = (text) => {
    setIsTyping(true);
    setTimeout(() => {
      setHistory((prev) => [...prev, { type: "bot", text }]);
      setIsTyping(false);
    }, 600);
  };

  const addUserMessage = (text) => {
    setHistory((prev) => [...prev, { type: "user", text }]);
  };

  const handleOptionClick = (option) => {
    // Adiciona a escolha do usuário ao histórico
    addUserMessage(option.label);

    // Verifica se é uma ação
    if (option.action) {
      handleAction(option.action);
      return;
    }

    // Navega para o próximo passo
    if (option.next && chatFlow[option.next]) {
      setCurrentStep(option.next);
      addBotMessage(chatFlow[option.next].message);
    } else {
      // Fallback se o passo não existir
      addBotMessage("Desculpe, não entendi. Vamos voltar ao início?");
      setCurrentStep("start");
    }
  };

  const handleAction = (action) => {
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);

      switch (action) {
        case "whatsapp_handoff":
          const text = encodeURIComponent("Olá! Preciso de ajuda com o PapIA e o chatbot não conseguiu resolver.");
          window.open(`https://wa.me/${SUPPORT_NUMBER}?text=${text}`, "_blank");
          setHistory((prev) => [...prev, { type: "bot", text: "Estou te redirecionando para o WhatsApp... 🚀" }]);
          break;

        case "close_chat":
          setIsOpen(false);
          break;

        default:
          break;
      }
    }, 600);
  };

  const resetChat = () => {
    setHistory([]);
    setCurrentStep("start");
    addBotMessage(chatFlow.start.message);
  };

  // Obtém as opções do passo atual
  const currentOptions = chatFlow[currentStep]?.options || [];

  return (
    <div className={`support-chat-container ${isChatsPage ? 'chats-page' : ''}`}>
      {/* Hint de Ajuda */}
      {showHint && !isOpen && (
        <div
          className="support-hint"
          onClick={() => setIsOpen(true)}
        >
          Precisa de ajuda? Clique aqui
          <div className="support-hint-arrow"></div>
        </div>
      )}

      {/* Botão Flutuante (FAB) */}
      <button
        className={`support-fab ${isOpen ? 'active' : ''}`}
        onClick={() => {
          setIsOpen(!isOpen);
          setShowHint(false);
        }}
        aria-label={isOpen ? "Fechar Suporte" : "Abrir Suporte"}
        style={{ zIndex: 10000 }}
      >
        {isOpen ? <X size={24} /> : <Headphones size={30} />}
      </button>

      {/* Janela do Chat */}
      {isOpen && (
        <div className="support-window">
          {/* Cabeçalho */}
          <div className="chat-header">
            <div className="chat-header-title">
              <Headphones size={18} />
              <span>Suporte PapIA</span>
            </div>
            <div className="flex gap-2">
              <button
                className="chat-close-btn"
                onClick={resetChat}
                title="Reiniciar conversa"
              >
                <RefreshCw size={16} />
              </button>
              {/* Botão de fechar removido do header pois já existe no FAB, 
                  mas podemos manter como opção secundária ou remover para limpar a UI. 
                  Vou manter apenas o refresh aqui já que o X está no FAB agora. */}
            </div>
          </div>

          {/* Área de Mensagens */}
          <div className="chat-messages">
            {history.map((msg, index) => (
              <div key={index} className={`message ${msg.type}`}>
                {msg.text.split('\n').map((line, i) => (
                  <span key={i}>
                    {line}
                    <br />
                  </span>
                ))}
              </div>
            ))}

            {isTyping && (
              <div className="typing-indicator">
                <div className="typing-dot"></div>
                <div className="typing-dot"></div>
                <div className="typing-dot"></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Área de Opções (Rodapé interativo) */}
          {!isTyping && currentOptions.length > 0 && (
            <div className="chat-footer">
              <p className="mb-2">Escolha uma opção:</p>
              <div className="chat-options">
                {currentOptions.map((option, index) => (
                  <button
                    key={index}
                    className="option-btn"
                    onClick={() => handleOptionClick(option)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SupportChat;
